import { PROMO } from '../promo';
// Área do jogo: desenha o mapa em 15x11 quadrados de 32 pixels com a câmera seguindo o personagem,
// na mesma ordem de desenho do cliente (chão, bordas, paredes, itens, criaturas, topo).
import { ELITE } from '../../../shared/src/game/elite';
import { Application, Assets, Container, Graphics, Rectangle, Sprite, Text, Texture, TextureStyle } from 'pixi.js';
import type { Outfit } from '../../../shared/src/protocol';
import { DIR_DELTA, lookDir, stepDuration, type Creature } from '../../../shared/src/game/world';
import { loadOutfitData, renderMounted, renderOutfit } from './outfit';
import { findPath, loadMap, mapObj, type ClientMap } from './map';
import { loadSheets } from './items';
import { HdView } from './hd';

const HD_KEY = 'noxara.hd';
export function hdEnabled() { try { return localStorage.getItem(HD_KEY) !== '0'; } catch { return true; } }
export function setHdEnabled(on: boolean) { try { localStorage.setItem(HD_KEY, on ? '1' : '0'); } catch { /* */ } }

export const VIEW_TILES_X = 15;
export const VIEW_TILES_Y = 11;
const T = 32;
const VIEW_W = VIEW_TILES_X * T, VIEW_H = VIEW_TILES_Y * T;

interface Sheet { sheet: string; first: number; last: number; w: number; h: number; cols: number }
interface Walk { fx: number; fy: number; start: number; dur: number }
interface CView {
  c: Creature;
  walk: Walk | null;
  label: Text; bar: Graphics;
  /** caveira do PvP: o ícone oficial do cliente (creature-state-flags.png) */
  skull?: Sprite;
  steps: number;
  /** passos vindos do servidor: hora do último e intervalo médio (a animação acompanha o ritmo real, sem pausas) */
  lastMv?: number; avgMv?: number;
}
interface OutfitTex { idle: Texture[][]; walk: Texture[][]; shift: [number, number]; always: boolean }
interface Hit { id: string; value: number; color: number; start: number; text: Text; say?: boolean }

// --- efeitos e projéteis ----------------------------------------------------------------
interface FxDef { s: number[]; pw?: number; ph?: number; a?: number[] }
let fxData: { effects: Record<string, FxDef>; missiles: Record<string, FxDef> } | null = null;
fetch('/data/effects.json', { cache: 'no-cache' }).then((r) => r.json()).then((d) => { fxData = d; }).catch(() => {});
interface Shot { x0: number; y0: number; x1: number; y1: number; id: number; start: number; dur: number; f: number }
interface Eff { x: number; y: number; id: number; start: number; f: number }

// --- texturas a partir das folhas ------------------------------------------------------
let sheets: Sheet[] = [];
const sheetTex = new Map<string, Texture | 'loading'>();
// caveiras do PvP como no cliente do Tibia: folha creature-state-flags.png, 2ª linha a partir da 2ª coluna, ícones 11x11
// (amarela, verde, branca, vermelha, preta, laranja)
let skullSheet: Texture | 'loading' | null = null;
const skullTex = new Map<string, Texture>();
const SKULL_INDEX: Record<string, number> = { yellow: 0, green: 1, white: 2, red: 3, black: 4, orange: 5 };
function skullTexture(kind: string): Texture | null {
  if (skullSheet === null) {
    skullSheet = 'loading';
    Assets.load('/assets/interface/images/creatures/creature-state-flags.png').then((t: Texture) => { skullSheet = t; }).catch(() => { skullSheet = null; });
    return null;
  }
  if (skullSheet === 'loading') return null;
  const i = SKULL_INDEX[kind];
  if (i === undefined) return null;
  let t = skullTex.get(kind);
  if (!t) { t = new Texture({ source: skullSheet.source, frame: new Rectangle((i + 1) * 11, 11, 11, 11) }); skullTex.set(kind, t); }
  return t;
}
const spriteTex = new Map<number, Texture>();
function findSheet(id: number): Sheet | undefined {
  let lo = 0, hi = sheets.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1, s = sheets[mid]!;
    if (id < s.first) hi = mid - 1; else if (id > s.last) lo = mid + 1; else return s;
  }
  return undefined;
}
function tex(id: number): Texture | null {
  const t = spriteTex.get(id);
  if (t) return t;
  const s = findSheet(id);
  if (!s) return null;
  const base = sheetTex.get(s.sheet);
  if (base === undefined) {
    sheetTex.set(s.sheet, 'loading');
    Assets.load(s.sheet.startsWith('custom/') ? '/' + s.sheet : '/assets/sheets/' + s.sheet).then((tx: Texture) => sheetTex.set(s.sheet, tx)).catch(() => sheetTex.delete(s.sheet));
    return null;
  }
  if (base === 'loading') return null;
  const i = id - s.first;
  const sub = new Texture({ source: base.source, frame: new Rectangle((i % s.cols) * s.w, Math.floor(i / s.cols) * s.h, s.w, s.h) });
  spriteTex.set(id, sub);
  return sub;
}

const outfitCache = new Map<string, Promise<OutfitTex>>();
function outfitTextures(o: Outfit): Promise<OutfitTex> {
  const key = `${o.lookType}:${o.head}:${o.body}:${o.legs}:${o.feet}:${o.addons}:${o.mount ?? 0}`;
  let p = outfitCache.get(key);
  if (!p) {
    p = (async () => {
      const data = await loadOutfitData();
      // montado: a animação segue a da montaria
      const entry = data.outfits[String(o.mount || o.lookType)] as any;
      const draw = (d: number, g: number, ph: number) => (o.mount ? renderMounted(o, o.mount, d, g, ph) : renderOutfit(o, d, g, ph));
      const groups = entry?.groups ?? [];
      const always = !!entry?.always;
      const idlePhases = always ? Math.max(1, groups[0]?.phases ?? 1) : 1;
      const walkGroup = groups.length > 1 ? 1 : 0;
      const walkPhases = Math.max(1, Math.min(8, groups[walkGroup]?.phases ?? 1));
      const idle: Texture[][] = [], walk: Texture[][] = [];
      for (let d = 0; d < 4; d++) {
        const ir: Texture[] = [];
        for (let ph = 0; ph < idlePhases; ph++) ir.push(Texture.from(await draw(d, 0, ph)));
        idle.push(ir);
        const row: Texture[] = [];
        for (let ph = 0; ph < walkPhases; ph++) row.push(Texture.from(await draw(d, walkGroup, ph)));
        walk.push(row);
      }
      return { idle, walk, shift: entry?.shift ?? [0, 0], always };
    })();
    outfitCache.set(key, p);
  }
  return p;
}
const outfitReady = new Map<string, OutfitTex>();
function outfitNow(o: Outfit): OutfitTex | null {
  const key = `${o.lookType}:${o.head}:${o.body}:${o.legs}:${o.feet}:${o.addons}:${o.mount ?? 0}`;
  const r = outfitReady.get(key);
  if (r) return r;
  outfitTextures(o).then((t) => outfitReady.set(key, t)).catch((e) => console.warn('outfit', key, e));
  return null;
}

export function hpColor(pct: number) {
  return pct > 92 ? 0x00c000 : pct > 60 ? 0x60c060 : pct > 30 ? 0xc0c000 : pct > 8 ? 0xc03030 : 0xc00000;
}

export interface ViewEvents {
  send: (m: { t: 'step'; dir: number } | { t: 'turn'; dir: number }) => void;
  onTile: (x: number, y: number, f: number, map: ClientMap) => void;
  onMapLoaded: (map: ClientMap) => void;
  attack: (id: string | null) => void;
  /** conversar com um NPC (já perto dele) */
  talk: (id: string) => void;
  /** puxar a alavanca de uma quest (já ao lado dela) */
  lever: (x: number, y: number, f: number) => void;
  onCreatures: (list: Creature[], target: string | null) => void;
  /** "Look" do Tibia numa criatura (botão direito, Shift+clique, dois cliques ou toque longo) */
  look: (c: Creature) => void;
}

export class WorldView {
  private app = new Application();
  private ready: Promise<void>;
  private destroyed = false;
  private root = new Container();
  private scene = new Container();
  private overlay = new Container();
  private pool: Sprite[] = [];
  private hd: HdView | null = null;
  private used = 0;
  private map: ClientMap | null = null;
  private mapName = '';
  private me: CView | null = null;
  private others = new Map<string, CView>();
  private queue: number[] = [];
  /** NPC clicado de longe: anda até ele e conversa ao chegar */
  private pendingTalk: string | null = null;
  private pendingLever: number[] | null = null;
  private held: number | null = null;
  private keyHandler = (e: KeyboardEvent) => this.onKey(e);
  private keyUp = (e: KeyboardEvent) => { if (this.keyDir(e) === this.held) this.held = null; };
  private lastTile = '';
  private pendingWorld: { map: string; x: number; y: number; f: number; dir: number } | null = null;
  private hits: Hit[] = [];
  private target: string | null = null;
  private targetBox = new Graphics();
  private lastList = 0;
  private signs: Array<{ x: number; y: number; f: number; text: Text }> = [];
  private shots: Shot[] = [];
  private effs: Eff[] = [];

  constructor(private host: HTMLElement, private ev: ViewEvents) {
    TextureStyle.defaultOptions.scaleMode = 'nearest';
    this.ready = (async () => {
      sheets = await loadSheets();
      await this.app.init({ resizeTo: host, background: '#000000', antialias: false, autoDensity: true, resolution: window.devicePixelRatio || 1 });
      if (this.destroyed) return;
      host.appendChild(this.app.canvas);
      this.overlay.addChild(this.targetBox);
      this.root.addChild(this.overlay, this.maskG);
      this.root.mask = this.maskG;
      this.app.stage.addChild(this.root);
      this.fit();
      this.app.renderer.on('resize', () => this.fit());
      this.app.ticker.add(() => this.frame());
      this.app.canvas.addEventListener('pointerdown', (e: PointerEvent) => this.onPointer(e));
      this.app.canvas.addEventListener('contextmenu', (e: Event) => e.preventDefault());
      this.app.canvas.addEventListener('dblclick', (e: MouseEvent) => this.lookAt(e.offsetX, e.offsetY));
      // celular: toque longo (sem arrastar) = look
      const cancel = () => { if (this.pressT) { clearTimeout(this.pressT); this.pressT = 0; } };
      this.app.canvas.addEventListener('pointermove', (e: PointerEvent) => { if (this.pressT && Math.hypot(e.offsetX - this.pressAt[0], e.offsetY - this.pressAt[1]) > 12) cancel(); });
      this.app.canvas.addEventListener('pointerup', (e: PointerEvent) => {
        if (e.pointerType !== 'touch' || !this.pressT) return;
        cancel();
        // decide pela duração real do toque (o timer pode atrasar com o celular ocupado)
        if (e.timeStamp - this.pressDown >= 480) this.lookAt(this.pressAt[0]!, this.pressAt[1]!);
        else this.tap(this.pressAt[0]!, this.pressAt[1]!);
      });
      this.app.canvas.addEventListener('pointercancel', cancel);
      window.addEventListener('keydown', this.keyHandler);
      window.addEventListener('keyup', this.keyUp);
    })();
  }

  /**
   * Ocupa TODA a área do jogo, sem faixas pretas (pedido do Arthur, 15/10): no computador a altura mostra 11 quadrados
   * e a largura quantos couberem; no celular em pé a largura mostra 9 quadrados e a altura quantos couberem (até 15,
   * o alcance de visão do servidor). Os quadrados das bordas podem aparecer cortados; o personagem fica sempre no centro.
   */
  private fit() {
    const w = this.app.screen.width, h = this.app.screen.height;
    const odd = (n: number) => (n % 2 === 0 ? n + 1 : n);
    let sc: number, tx: number, ty: number;
    if (w >= h) {
      sc = h / VIEW_H;
      tx = Math.min(31, odd(Math.ceil(w / (sc * T))));
      ty = VIEW_TILES_Y;
      if (tx * T * sc < w) sc = w / (tx * T);   // tela muito larga: aumenta um pouco o zoom para cobrir
    } else {
      sc = w / (9 * T);
      ty = odd(Math.ceil(h / (sc * T)));
      if (ty > 15) { ty = 15; sc = h / (ty * T); }
      tx = Math.min(31, odd(Math.ceil(w / (sc * T))));
    }
    ty = Math.max(ty, Math.min(VIEW_TILES_Y, odd(Math.ceil(h / (sc * T)))));
    this.root.scale.set(sc);
    // como no cliente do Tibia, nomes, barras de vida e números têm tamanho fixo na tela (não crescem com o zoom)
    this.ts = 1 / sc;
    this.root.position.set(Math.round((w - tx * T * sc) / 2), Math.round((h - ty * T * sc) / 2));
    if (tx !== this.tilesX || ty !== this.tilesY || !this.viewReady) {
      this.tilesX = tx; this.tilesY = ty; this.viewReady = true;
      this.maskG.clear().rect(0, 0, tx * T, ty * T).fill(0xffffff);
      // visual HD: a textura do tamanho da área visível
      if (this.hd) { this.root.removeChild(this.hd.mesh); this.hd.texture.destroy(true); this.hd = null; }
      else this.root.removeChild(this.scene);
      if (hdEnabled()) {
        try { this.hd = new HdView(tx * T, ty * T); } catch (e) { console.warn('visual HD indisponível', e); this.hd = null; }
      }
      this.root.addChildAt(this.hd ? this.hd.mesh : this.scene, 0);
    }
    this.hd?.setScale(sc * (window.devicePixelRatio || 1));
  }
  private tilesX = VIEW_TILES_X;
  private tilesY = VIEW_TILES_Y;
  private get halfY() { return (this.tilesY - 1) / 2; }
  /** escala dos textos (1 / zoom do mapa) */
  private ts = 1;
  private viewReady = false;
  private maskG = new Graphics();
  private get half() { return (this.tilesX - 1) / 2; }

  private makeView(c: Creature): CView {
    const label = new Text({
      text: PROMO && c.kind === 'player' ? '' : c.name,
      style: { fontFamily: 'Verdana, Tahoma, Arial, sans-serif', fontSize: 11, fontWeight: 'bold', fill: hpColor(c.hp), stroke: { color: '#000', width: 2 }, letterSpacing: 0 },
      resolution: 4,
    });
    label.anchor.set(0.5, 1);
    const bar = new Graphics();
    this.overlay.addChild(label, bar);
    return { c, walk: null, label, bar, steps: 0 };
  }

  private dropView(v: CView) { v.label.destroy(); v.bar.destroy(); v.skull?.destroy(); }

  // --- mensagens do servidor ----------------------------------------------------------
  /** campos das runas no chão (tile -> objeto) */
  private fieldMap = new Map<string, number>();
  setFields(list: number[][]) { this.fieldMap = new Map(list.map(([x, y, f, id]) => [`${x},${y},${f}`, id!])); }
  /** decoração da casa (itens colocados pelo dono) */
  private decorMap = new Map<string, number>();
  setDecor(list: number[][]) { this.decorMap = new Map(list.map(([x, y, f, id]) => [`${x},${y},${f}`, id!])); }

  async setWorld(m: { map: string; x: number; y: number; f: number; dir: number }, self: Creature) {
    this.fieldMap = new Map();
    if (!m.map.startsWith('house-')) this.decorMap = new Map();
    this.pendingWorld = m;
    const change = m.map !== this.mapName;
    if (change) {
      // limpa as criaturas do mapa antigo já agora (síncrono): as do mapa novo chegam logo em seguida
      this.mapName = m.map;
      this.map = null;
      for (const v of this.others.values()) this.dropView(v);
      this.others.clear();
    }
    await this.ready;
    if (change) {
      const map = await loadMap(m.map);
      if (this.pendingWorld !== m) return; // chegou outra mudança enquanto carregava
      this.map = map;
      this.setSigns([]);
      this.ev.onMapLoaded(map);
    }
    if (this.pendingWorld !== m) return;
    if (!this.me) this.me = this.makeView({ ...self, x: m.x, y: m.y, f: m.f, dir: m.dir });
    this.me.c = { ...this.me.c, ...self, ...this.selfPatch, x: m.x, y: m.y, f: m.f, dir: lookDir(m.dir) };
    this.me.walk = null;
    this.queue = [];
    this.pendingWorld = null;
  }

  correct(x: number, y: number, f: number, dir: number) {
    if (!this.me) return;
    this.me.c.x = x; this.me.c.y = y; this.me.c.f = f; this.me.c.dir = lookDir(dir);
    this.me.walk = null;
    this.queue = [];
  }

  /** aura/outfit do próprio personagem: guardado também para quando o mapa ainda está carregando (o "me" ainda não existe) */
  private selfPatch: Partial<Creature> = {};
  updateSelf(patch: Partial<Creature>) { this.selfPatch = { ...this.selfPatch, ...patch }; if (this.me) Object.assign(this.me.c, patch); }

  setCreatures(list: Creature[]) {
    for (const v of this.others.values()) this.dropView(v);
    this.others.clear();
    for (const c of list) this.others.set(c.id, this.makeView(c));
  }
  addCreature(c: Creature) {
    if (this.me && c.id === this.me.c.id) { this.me.c.hp = c.hp; this.me.c.outfit = c.outfit; this.me.c.aura = c.aura; this.me.c.skull = c.skull; return; }
    const old = this.others.get(c.id);
    if (old) { old.c.hp = c.hp; old.c.outfit = c.outfit; old.c.name = c.name; old.c.speed = c.speed; old.c.aura = c.aura; old.c.skull = c.skull; return; }
    this.others.set(c.id, this.makeView(c));
  }
  removeCreature(id: string) {
    const v = this.others.get(id);
    if (v) { this.dropView(v); this.others.delete(id); }
    if (this.target === id) this.target = null;
  }

  setTarget(id: string | null) { this.target = id; }

  addFx(f: number, shots: number[][] = [], effects: number[][] = []) {
    const now = performance.now();
    for (const [x0, y0, x1, y1, id] of shots) {
      const dist = Math.max(Math.abs(x1! - x0!), Math.abs(y1! - y0!));
      this.shots.push({ x0: x0!, y0: y0!, x1: x1!, y1: y1!, id: id!, start: now, dur: 80 + dist * 55, f });
    }
    // o efeito de impacto espera o projétil chegar
    const delay = shots.length ? Math.max(...this.shots.slice(-shots.length).map((s) => s.dur)) : 0;
    for (const [x, y, id] of effects) this.effs.push({ x: x!, y: y!, id: id!, start: now + delay, f });
    if (this.effs.length > 80) this.effs.splice(0, this.effs.length - 80);
  }

  private drawFx(now: number, f: number) {
    if (!fxData) return;
    this.shots = this.shots.filter((s) => now - s.start < s.dur);
    for (const s of this.shots) {
      if (s.f !== f) continue;
      const def = fxData.missiles[s.id];
      if (!def) continue;
      const dx = Math.sign(s.x1 - s.x0), dy = Math.sign(s.y1 - s.y0);
      const t = tex(def.s[(dy + 1) * (def.pw ?? 3) + (dx + 1)] ?? def.s[0]!);
      if (!t) continue;
      const p = (now - s.start) / s.dur;
      const x = s.x0 + (s.x1 - s.x0) * p, y = s.y0 + (s.y1 - s.y0) * p;
      this.put(t, x * T + T - t.frame.width, y * T + T - t.frame.height);
    }
    const keep: Eff[] = [];
    for (const e of this.effs) {
      const def = fxData.effects[e.id];
      if (!def) continue;
      const age = now - e.start;
      const phases = def.a ?? [100];
      const total = phases.reduce((a, b) => a + b, 0);
      if (age > total) continue;
      keep.push(e);
      if (age < 0 || e.f !== f) continue;
      let acc = 0, ph = 0;
      while (ph < phases.length - 1 && acc + phases[ph]! <= age) acc += phases[ph++]!;
      const t = tex(def.s[ph] ?? def.s[0]!);
      if (t) this.put(t, e.x * T + T - t.frame.width, e.y * T + T - t.frame.height);
    }
    this.effs = keep;
  }

  /** Nomes escritos sobre o mapa (ex.: a hunt de cada portal). */
  setSigns(list: Array<{ x: number; y: number; f: number; text: string; color?: number }>) {
    for (const s of this.signs) s.text.destroy();
    this.signs = list.map((l) => {
      const text = new Text({
        text: l.text,
        style: { fontFamily: 'Verdana, Tahoma, Arial, sans-serif', fontSize: 9, fontWeight: 'bold', fill: l.color ?? 0xe8d9a8, stroke: { color: '#000', width: 3 }, align: 'center' },
        resolution: 4,
      });
      text.anchor.set(0.5, 1);
      text.visible = false;
      this.overlay.addChild(text);
      return { x: l.x, y: l.y, f: l.f, text };
    });
  }

  /** Número que sobe da criatura (dano, cura, experiência), como no cliente. */
  addHit(id: string, value: number, color: number) {
    const text = new Text({
      text: value === 0 ? 'miss' : String(value),
      style: { fontFamily: 'Verdana, Tahoma, Arial, sans-serif', fontSize: 11, fontWeight: 'bold', fill: color, stroke: { color: '#000', width: 2 } },
      resolution: 4,
    });
    text.anchor.set(0.5, 1);
    this.overlay.addChild(text);
    this.hits.push({ id, value, color, start: performance.now(), text });
    if (this.hits.length > 40) this.hits.shift()!.text.destroy();
  }
  /** Palavras da magia (laranja) sobre a criatura, paradas por 2 s, como no cliente do Tibia */
  addSay(id: string, words: string, color: number) {
    for (const h of this.hits) if (h.say && h.id === id) h.start = -1e9;   // a nova fala substitui a anterior
    const text = new Text({
      text: words,
      style: { fontFamily: 'Verdana, Tahoma, Arial, sans-serif', fontSize: 11, fontWeight: 'bold', fill: color, stroke: { color: '#000', width: 2 } },
      resolution: 4,
    });
    text.anchor.set(0.5, 1);
    this.overlay.addChild(text);
    this.hits.push({ id, value: 0, color, start: performance.now(), text, say: true });
    if (this.hits.length > 40) this.hits.shift()!.text.destroy();
  }
  moveCreature(id: string, x: number, y: number, f: number, dir: number) {
    const v = this.me && id === this.me.c.id ? this.me : this.others.get(id);
    if (!v) return;
    const dx = x - v.c.x, dy = y - v.c.y;
    if (f === v.c.f && Math.abs(dx) <= 1 && Math.abs(dy) <= 1 && (dx || dy)) {
      const g = this.map?.groundSpeed(v.c.x, v.c.y, f) ?? 150, now = performance.now(), diag = dx !== 0 && dy !== 0;
      let dur = stepDuration(g, v.c.speed, diag);
      // andando sem parar: usa o ritmo em que os passos realmente chegam (servidor + rede), para não andar-parar-andar
      const gap = v.lastMv ? now - v.lastMv : Infinity;
      if (gap < dur * 2.2) {
        v.avgMv = v.avgMv ? v.avgMv * 0.6 + gap / (diag ? 2 : 1) * 0.4 : gap / (diag ? 2 : 1);
        dur = Math.max(dur * 0.75, Math.min(dur * 1.6, v.avgMv * (diag ? 2 : 1)));
      } else v.avgMv = undefined;
      v.lastMv = now;
      // passo novo chegou antes do anterior terminar: começa de onde o boneco está desenhado (sem pulo)
      let fx = v.c.x, fy = v.c.y;
      if (v.walk && now - v.walk.start < v.walk.dur) {
        const t = (now - v.walk.start) / v.walk.dur;
        fx = v.walk.fx + (v.c.x - v.walk.fx) * t; fy = v.walk.fy + (v.c.y - v.walk.fy) * t;
      }
      v.walk = { fx, fy, start: now, dur };
      v.steps++;
    } else { v.walk = null; v.lastMv = undefined; v.avgMv = undefined; }
    v.c.x = x; v.c.y = y; v.c.f = f; v.c.dir = dir;
  }

  // --- controle ---------------------------------------------------------------------------
  private keyDir(e: KeyboardEvent): number | null {
    switch (e.key) {
      case 'ArrowUp': case 'w': case 'W': return 0;
      case 'ArrowRight': case 'd': case 'D': return 1;
      case 'ArrowDown': case 's': case 'S': return 2;
      case 'ArrowLeft': case 'a': case 'A': return 3;
      case 'PageUp': case '9': return e.code.startsWith('Numpad') || e.key === 'PageUp' ? 4 : null;
      case 'PageDown': case '3': return e.code.startsWith('Numpad') || e.key === 'PageDown' ? 5 : null;
      case 'End': case '1': return e.code.startsWith('Numpad') || e.key === 'End' ? 6 : null;
      case 'Home': case '7': return e.code.startsWith('Numpad') || e.key === 'Home' ? 7 : null;
      case '8': return e.code === 'Numpad8' ? 0 : null;
      case '6': return e.code === 'Numpad6' ? 1 : null;
      case '2': return e.code === 'Numpad2' ? 2 : null;
      case '4': return e.code === 'Numpad4' ? 3 : null;
    }
    return null;
  }

  private onKey(e: KeyboardEvent) {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    const d = this.keyDir(e);
    if (d === null) { if (e.key === 'Escape') this.queue = []; return; }
    e.preventDefault();
    if (e.ctrlKey && d < 4) { if (this.me) { this.me.c.dir = d; this.ev.send({ t: 'turn', dir: d }); } return; }
    this.queue = [];
    this.pendingTalk = null; this.pendingLever = null;
    this.held = d;
  }

  private pressT = 0;
  private pressAt = [0, 0];
  private pressDown = 0;
  /** look na criatura embaixo do ponteiro (pela posição desenhada, então funciona com ela andando) */
  private lookAt(ox: number, oy: number) {
    if (!this.me) return;
    const p = this.root.toLocal({ x: ox, y: oy });
    if (p.x < 0 || p.y < 0 || p.x > this.tilesX * T || p.y > this.tilesY * T) return;
    const { cx, cy } = this.camera();
    const fx = p.x / T + cx - this.half, fy = p.y / T + cy - this.halfY, now = performance.now();
    let best: CView | null = null, bd = Infinity;
    for (const v of [this.me, ...this.others.values()]) {
      if (v.c.f !== this.me.c.f) continue;
      const [rx, ry] = this.renderPos(v, now);
      // o corpo desenhado vai um pouco para cima/esquerda do quadrado (bonecos de 64 px)
      if (fx < rx - 0.45 || fx > rx + 1.1 || fy < ry - 0.6 || fy > ry + 1.1) continue;
      const d = Math.hypot(fx - (rx + 0.4), fy - (ry + 0.35));
      if (d < bd) { bd = d; best = v; }
    }
    if (best) this.ev.look(best.c);
  }

  private onPointer(e: PointerEvent) {
    if (!this.map || !this.me) return;
    if (e.button === 2 || (e.button === 0 && e.shiftKey)) { this.lookAt(e.offsetX, e.offsetY); return; }
    if (e.button !== 0) return;
    if (e.pointerType === 'touch') {
      // celular: toque curto = ação normal (ao soltar); segurar = look (sem andar nem pausar a caça)
      if (this.pressT) clearTimeout(this.pressT);
      const ox = e.offsetX, oy = e.offsetY;
      this.pressAt = [ox, oy]; this.pressDown = e.timeStamp;
      this.pressT = window.setTimeout(() => { this.pressT = 0; this.lookAt(ox, oy); navigator.vibrate?.(15); }, 480);
      return;
    }
    this.tap(e.offsetX, e.offsetY);
  }

  private tap(ox: number, oy: number) {
    if (!this.map || !this.me) return;
    const p = this.root.toLocal({ x: ox, y: oy });
    if (p.x < 0 || p.y < 0 || p.x > this.tilesX * T || p.y > this.tilesY * T) return;
    const { cx, cy } = this.camera();
    const tx = Math.floor((p.x + (cx - this.half) * T) / T), ty = Math.floor((p.y + (cy - this.halfY) * T) / T);
    // clicou num monstro: ataca (como o clique com alvo do cliente)
    for (const v of this.others.values()) {
      if (v.c.kind === 'monster' && v.c.f === this.me.c.f && v.c.x === tx && v.c.y === ty) {
        this.target = v.c.id; this.ev.attack(v.c.id); return;
      }
    }
    // clicou num NPC: conversa (anda até perto se estiver longe)
    for (const v of this.others.values()) {
      if (v.c.kind !== 'npc' || v.c.f !== this.me.c.f || v.c.x !== tx || v.c.y !== ty) continue;
      if (Math.max(Math.abs(v.c.x - this.me.c.x), Math.abs(v.c.y - this.me.c.y)) <= 3) { this.pendingTalk = null; this.ev.talk(v.c.id); return; }
      let best: number[] | null = null;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const pth = findPath(this.map, this.me.c.f, this.me.c.x, this.me.c.y, tx + dx, ty + dy);
        if (pth && (!best || pth.length < best.length)) best = pth;
      }
      if (best) { this.queue = best; this.pendingTalk = v.c.id; }
      return;
    }
    // clicou numa alavanca de quest: vai até o primeiro ladrilho e puxa
    const levers = (this.map.meta.points['levers'] ?? []) as Array<{ pos: number[]; tiles: number[][] }>;
    const lv = levers.find((l) => l.pos[0] === tx && l.pos[1] === ty && (l.pos[2] ?? 0) === this.me!.c.f);
    if (lv) {
      const t0 = lv.tiles[0]!;
      this.pendingTalk = null;
      if (this.me.c.x === t0[0] && this.me.c.y === t0[1]) { this.ev.lever(lv.pos[0]!, lv.pos[1]!, lv.pos[2] ?? 0); return; }
      const pth = findPath(this.map, this.me.c.f, this.me.c.x, this.me.c.y, t0[0]!, t0[1]!);
      if (pth) { this.queue = pth; this.pendingLever = lv.pos; }
      return;
    }
    this.pendingTalk = null; this.pendingLever = null;
    const path = findPath(this.map, this.me.c.f, this.me.c.x, this.me.c.y, tx, ty);
    if (path) this.queue = path;
  }

  private tryStep(now: number) {
    const me = this.me, map = this.map;
    if (!me || !map) return;
    if (me.walk && now - me.walk.start < me.walk.dur) return;
    me.walk = null;
    if (this.pendingTalk && !this.queue.length) {
      const n = this.others.get(this.pendingTalk);
      if (n && Math.max(Math.abs(n.c.x - me.c.x), Math.abs(n.c.y - me.c.y)) <= 3) this.ev.talk(n.c.id);
      this.pendingTalk = null;
    }
    if (this.pendingLever && !this.queue.length) {
      const lv = this.pendingLever; this.pendingLever = null;
      this.ev.lever(lv[0]!, lv[1]!, lv[2] ?? 0);
    }
    const dir = this.queue.length ? this.queue[0]! : this.held;
    if (dir === null || dir === undefined) return;
    if (this.queue.length) this.queue.shift();
    const [dx, dy] = DIR_DELTA[dir]!;
    const nx = me.c.x + dx, ny = me.c.y + dy;
    if (!map.walkable(nx, ny, me.c.f)) {
      this.queue = [];
      if (dir < 4 && me.c.dir !== dir) { me.c.dir = dir; this.ev.send({ t: 'turn', dir }); }
      return;
    }
    const g = map.groundSpeed(me.c.x, me.c.y, me.c.f);
    me.walk = { fx: me.c.x, fy: me.c.y, start: now, dur: stepDuration(g, me.c.speed, dir > 3) };
    me.steps++;
    me.c.x = nx; me.c.y = ny; me.c.dir = lookDir(dir);
    this.ev.send({ t: 'step', dir });
  }

  // --- desenho ------------------------------------------------------------------------------
  private renderPos(v: CView, now: number): [number, number, number] {
    if (!v.walk) return [v.c.x, v.c.y, 0];
    const p = Math.min(1, (now - v.walk.start) / v.walk.dur);
    if (p >= 1) { v.walk = null; return [v.c.x, v.c.y, 0]; }
    return [v.walk.fx + (v.c.x - v.walk.fx) * p, v.walk.fy + (v.c.y - v.walk.fy) * p, p];
  }

  private camera() {
    if (!this.me) return { cx: 0, cy: 0 };
    const [x, y] = this.renderPos(this.me, performance.now());
    return { cx: x, cy: y };
  }

  private put(t: Texture, x: number, y: number, tint = 0xffffff, alpha = 1, add = false, sc = 1) {
    let s = this.pool[this.used];
    if (!s) { s = new Sprite(t); this.pool.push(s); this.scene.addChild(s); }
    else s.texture = t;
    s.visible = true;
    s.position.set(x, y);
    s.tint = tint; s.alpha = alpha; s.blendMode = add ? 'add' : 'normal'; s.scale.set(sc);
    this.used++;
  }

  private auras: Array<{ x: number; y: number; color: number; tier: 1 | 2 | 3; seed: number }> = [];
  /** Aura dos monstros especiais: anel brilhante pulsando nos pés e faíscas subindo (mais faíscas quanto maior o tipo). */
  private drawAuras(now: number) {
    const g = this.glowHalves()[1], sp = this.spark();
    for (const a of this.auras) {
      const pulse = 0.5 + 0.5 * Math.sin(now / 280 + a.seed);
      this.put(g, a.x - 32, a.y, a.color, 0.5 + 0.4 * pulse, true);
      const n = a.tier * 2 + 1;
      for (let k = 0; k < n; k++) {
        const ph = ((now / 1400 + k / n + a.seed * 0.13) % 1);
        const ang = (k / n) * Math.PI * 2 + a.seed;
        const px = a.x + Math.cos(ang) * 13, py = a.y - 4 + Math.sin(ang) * 5 - ph * 34;
        this.put(sp, px - 4, py - 4, a.color, Math.sin(ph * Math.PI) * 0.95, true);
      }
    }
    this.auras = [];
  }
  private sparkTex: Texture | null = null;
  private spark(): Texture {
    if (this.sparkTex) return this.sparkTex;
    const cv = document.createElement('canvas'); cv.width = 8; cv.height = 8;
    const g = cv.getContext('2d')!;
    const grad = g.createRadialGradient(4, 4, 0, 4, 4, 4);
    grad.addColorStop(0, 'rgba(255,255,255,1)'); grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad; g.fillRect(0, 0, 8, 8);
    this.sparkTex = Texture.from(cv);
    return this.sparkTex;
  }

  /** Brilho redondo usado na aura dos monstros especiais (Super/Mega/Ultra). */
  private glowTex: Texture | null = null;
  private glowHalf: [Texture, Texture] | null = null;
  /** Metade de trás (em cima) e da frente (embaixo) do anel: a de trás fica atrás do monstro, a da frente na frente. */
  private glowHalves(): [Texture, Texture] {
    if (this.glowHalf) return this.glowHalf;
    const full = this.glow();
    this.glowHalf = [new Texture({ source: full.source, frame: new Rectangle(0, 0, 64, 32) }), new Texture({ source: full.source, frame: new Rectangle(0, 32, 64, 32) })];
    return this.glowHalf;
  }
  private glow(): Texture {
    if (this.glowTex) return this.glowTex;
    // halo oval no chão (como as auras do Tibia), mais forte na borda
    const cv = document.createElement('canvas'); cv.width = 64; cv.height = 64;
    const g = cv.getContext('2d')!;
    g.translate(32, 32); g.scale(1, 0.5);
    const grad = g.createRadialGradient(0, 0, 0, 0, 0, 22);
    grad.addColorStop(0, 'rgba(255,255,255,0)'); grad.addColorStop(0.45, 'rgba(255,255,255,0.08)');
    grad.addColorStop(0.62, 'rgba(255,255,255,0.55)'); grad.addColorStop(0.72, 'rgba(255,255,255,0.95)');
    grad.addColorStop(0.85, 'rgba(255,255,255,0.35)'); grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad; g.beginPath(); g.arc(0, 0, 22, 0, Math.PI * 2); g.fill();
    this.glowTex = Texture.from(cv);
    return this.glowTex;
  }

  /** Versão "metálica" de um quadro do monstro (bronze, prata ou ouro), feita uma vez e guardada. */
  private metal = new Map<string, Texture>();
  private metalTex(t: Texture, tier: 1 | 2 | 3): Texture {
    const key = `${(t as any).uid}:${tier}`;
    const hit = this.metal.get(key);
    if (hit) return hit;
    const res = (t.source as any).resource;
    if (!res) return t;
    const fr = t.frame, cv = document.createElement('canvas');
    cv.width = fr.width; cv.height = fr.height;
    const g = cv.getContext('2d')!;
    g.drawImage(res, fr.x, fr.y, fr.width, fr.height, 0, 0, fr.width, fr.height);
    const img = g.getImageData(0, 0, fr.width, fr.height), d = img.data;
    const c = ELITE[tier].color, cr = (c >> 16) & 255, cg = (c >> 8) & 255, cb = c & 255;
    for (let i = 0; i < d.length; i += 4) {
      if (!d[i + 3]) continue;
      const lum = (0.3 * d[i]! + 0.59 * d[i + 1]! + 0.11 * d[i + 2]!) / 255;
      const k = 0.3 + 1.05 * lum, spec = Math.max(0, lum - 0.62) * 260;   // tom do metal + reflexo nas partes claras
      d[i] = Math.min(255, cr * k + spec); d[i + 1] = Math.min(255, cg * k + spec); d[i + 2] = Math.min(255, cb * k + spec);
    }
    g.putImageData(img, 0, 0);
    const out = Texture.from(cv);
    this.metal.set(key, out);
    return out;
  }

  private drawObject(id: number, x: number, y: number, f: number, elev: number, now: number, off = 0): number {
    const o = mapObj(id);
    if (!o) return elev;
    const pw = o.pw ?? 1, ph = o.ph ?? 1, pd = o.pd ?? 1, l = o.l ?? 1;
    const phase = o.a ? Math.floor(now / o.a[1]) % o.a[0] : 0;
    const idx = ((((phase * pd + (f % pd)) * ph + (y % ph)) * pw + (x % pw)) * l);
    const t = tex(o.s[idx] ?? o.s[0]!);
    if (t) {
      const w = t.frame.width, h = t.frame.height;
      this.put(t, (x + off) * T + T - w - (o.sh?.[0] ?? 0) - elev, (y + off) * T + T - h - (o.sh?.[1] ?? 0) - elev);
    }
    return Math.min(24, elev + (o.el ?? 0));
  }

  private drawCreature(v: CView, now: number, elev: number) {
    const ot = outfitNow(v.c.outfit);
    const [rx, ry, prog] = this.renderPos(v, now);
    const shift = ot ? ot.shift[0] : (v.c.kind === 'player' ? 8 : 0);
    const d = v.c.dir;
    const tier = v.c.tier ? ELITE[v.c.tier] : null;
    if (tier || v.c.aura) {
      // aura: brilho pulsante na cor do monstro especial (ou a aura cosmética do jogador), por baixo dele
      // metade de trás do anel agora (atrás do monstro); a da frente e as faíscas depois do mapa,
      // senão o chão dos tiles vizinhos as cobre
      const a = { x: rx * T + T / 2 - shift - elev, y: ry * T + T - 6 - shift - elev, color: tier ? tier.color : v.c.aura!, tier: (v.c.tier ?? 3) as 1 | 2 | 3, seed: tier ? v.c.x * 7 + v.c.y * 13 : 1.7 };
      const pulse = 0.5 + 0.5 * Math.sin(now / 280 + a.seed);
      this.put(this.glowHalves()[0], a.x - 32, a.y - 32, a.color, 0.5 + 0.4 * pulse, true);
      this.auras.push(a);
    }
    if (ot) {
      let t: Texture;
      if (v.walk) { const row = ot.walk[d]!; t = row[(Math.floor(prog * 4) + (v.steps % 2) * 4) % row.length]!; }
      else { const row = ot.idle[d]!; t = row[ot.always ? Math.floor(now / 140) % row.length : 0]!; }
      const px = rx * T + T - t.width - shift - elev, py = ry * T + T - t.height - shift - elev;
      if (tier) {
        // corpo na cor do metal (bronze, prata ou ouro) com reflexo brilhando por cima
        const mt = this.metalTex(t, v.c.tier!);
        this.put(mt, px, py);
        this.put(mt, px, py, 0xffffff, 0.05 + 0.07 * (0.5 + 0.5 * Math.sin(now / 200)), true);   // brilho do metal
      } else this.put(t, px, py);
    }
    // nome e barra de vida acima da cabeça
    // nome e barra ficam acima da cabeça, como no cliente (a cabeça de um outfit passa do topo do tile)
    const hx = rx * T + T / 2 - shift - elev, hy = ry * T - shift - elev - (v.c.outfit.mount ? 16 : 8);
    const ts = this.ts;
    v.label.scale.set(ts);
    v.label.position.set(hx, hy - 6 * ts - 1);
    (v.label.style as any).fill = v.c.kind === 'monster' ? hpColor(v.c.hp) : hpColor(v.c.hp);
    v.bar.clear();
    // barra de vida do Tibia: 27x4 pixels de tela, borda preta
    v.bar.rect(hx - 14 * ts, hy - 4 * ts, 28 * ts, 4 * ts).fill(0x000000);
    v.bar.rect(hx - 13 * ts, hy - 3 * ts, Math.max(0, 26 * ts * v.c.hp / 100), 2 * ts).fill(hpColor(v.c.hp));
    // caveira do PvP (white, red ou black) à direita do nome e da barra de vida, com o ícone do cliente do Tibia
    const sk = v.c.skull ? skullTexture(v.c.skull) : null;
    if (sk) {
      if (!v.skull) { v.skull = new Sprite(sk); this.overlay.addChild(v.skull); }
      v.skull.texture = sk;
      v.skull.scale.set(ts);
      v.skull.position.set(hx + 16 * ts, hy - 10 * ts);
      v.skull.visible = true;
    } else if (v.skull) v.skull.visible = false;
    v.label.visible = true; v.bar.visible = true;
    if (v.c.id === this.target) {
      this.targetBox.clear();
      this.targetBox.rect(rx * T - shift + 1 - elev, ry * T - shift + 1 - elev, T - 2, T - 2).stroke({ color: 0xff0000, width: 1 });
      this.targetBox.visible = true;
    }
  }

  private drawHits(now: number) {
    const keep: Hit[] = [];
    for (const h of this.hits) {
      const age = now - h.start;
      const v = this.me && h.id === this.me.c.id ? this.me : this.others.get(h.id);
      if (age > (h.say ? 2000 : 1000) || !v) { h.text.destroy(); continue; }
      const [rx, ry] = this.renderPos(v, now);
      h.text.scale.set(this.ts);
      if (h.say) { h.text.position.set(rx * T + 8, ry * T - 14); h.text.alpha = age > 1700 ? 1 - (age - 1700) / 300 : 1; keep.push(h); continue; }
      h.text.position.set(rx * T + 8, ry * T - 4 - age / 30);
      h.text.alpha = age > 700 ? 1 - (age - 700) / 300 : 1;
      keep.push(h);
    }
    this.hits = keep;
  }

  private frame() {
    const now = performance.now();
    this.tryStep(now);
    const map = this.map, me = this.me;
    this.used = 0;
    if (map && me) {
      const { cx, cy } = this.camera();
      const f = me.c.f;
      this.scene.position.set(-Math.round((cx - this.half) * T), -Math.round((cy - this.halfY) * T));
      this.overlay.position.copyFrom(this.scene.position);
      const x0 = Math.floor(cx) - this.half - 1, y0 = Math.floor(cy) - this.halfY - 1, x1 = Math.floor(cx) + this.half + 3, y1 = Math.floor(cy) + this.halfY + 3;
      this.targetBox.visible = false;
      const at = new Map<number, CView[]>();
      for (const v of [me, ...this.others.values()]) {
        v.label.visible = false; v.bar.visible = false; if (v.skull) v.skull.visible = false;
        if (v.c.f !== f) continue;
        // andando, a criatura é desenhada no tile que vem depois na ordem de desenho (origem ou destino),
        // senão o chão do tile de origem cobre metade dela ao andar para cima ou para a esquerda
        const wx = v.walk ? Math.ceil(Math.max(v.walk.fx, v.c.x)) : v.c.x, wy = v.walk ? Math.ceil(Math.max(v.walk.fy, v.c.y)) : v.c.y;
        const k = wy * 65536 + wx;
        (at.get(k) ?? at.set(k, []).get(k)!).push(v);
      }
      // andares de baixo aparecem pelos buracos e vãos do andar atual, como no Tibia: no subsolo até 2 andares abaixo,
      // na superfície até o térreo (andar 7); cada andar abaixo fica um tile para baixo e para a direita
      const z = (map.meta.zbase ?? 7) + f;
      // só nos mapas tirados do Tibia/Baiak (os gerados usam o vazio preto como rocha)
      const depth = (map.meta as any).hunt?.source ? Math.min(map.meta.floors - 1 - f, z > 7 ? 2 : Math.max(0, 7 - z), 2) : 0;
      const covered = (sx: number, sy: number, upto: number) => {   // o tile da tela já tem chão num andar mais alto
        for (let k = 0; k < upto; k++) { const s2 = map.stack(sx - k, sy - k, f + k), g = s2 && mapObj(s2[0]!); if (g && g.g && !(g as any).iv) return true; }
        return false;
      };
      for (let d = depth; d >= 1; d--) {
        for (let y = y0 - d; y <= y1 - d; y++) {
          for (let x = x0 - d; x <= x1 - d; x++) {
            if (covered(x + d, y + d, d)) continue;
            const st = map.stack(x, y, f + d);
            if (!st) continue;
            let elev = 0;
            for (let i = 0; i < st.length; i++) elev = this.drawObject(st[i]!, x, y, f + d, elev, now, d);
          }
        }
      }
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const st = map.stack(x, y, f);
          let elev = 0;
          if (st) for (let i = 0; i < st.length; i++) { const id = st[i]!; if (!mapObj(id)?.t) elev = this.drawObject(id, x, y, f, elev, now); }
          const fid = this.fieldMap.size ? this.fieldMap.get(`${x},${y},${f}`) : undefined;
          if (fid) this.drawObject(fid, x, y, f, elev, now);
          const did = this.decorMap.size ? this.decorMap.get(`${x},${y},${f}`) : undefined;
          if (did) this.drawObject(did, x, y, f, elev, now);
          const cs = at.get(y * 65536 + x);
          if (cs) for (const v of cs) this.drawCreature(v, now, elev);
          if (st) for (let i = 0; i < st.length; i++) { const id = st[i]!; if (mapObj(id)?.t) this.drawObject(id, x, y, f, 0, now); }
        }
      }
      this.drawAuras(now);
      this.drawFx(now, f);
      for (const sg of this.signs) {
        sg.text.visible = sg.f === f && Math.abs(sg.x - cx) <= this.half + 2 && Math.abs(sg.y - cy) <= this.halfY + 2;
        if (sg.text.visible) { sg.text.scale.set(this.ts * 1.1); sg.text.position.set(sg.x * T + T / 2, sg.y * T - 2); }
      }
      this.drawHits(now);
      if (now - this.lastList > 400) {
        this.lastList = now;
        const list = [...this.others.values()].map((v) => v.c)
          .filter((c) => c.f === f && Math.abs(c.x - me.c.x) <= this.half && Math.abs(c.y - me.c.y) <= this.halfY);
        this.ev.onCreatures(list, this.target);
      }
      const key = `${me.c.x},${me.c.y},${f}`;
      if (key !== this.lastTile) { this.lastTile = key; this.ev.onTile(me.c.x, me.c.y, f, map); }
    }
    for (let i = this.used; i < this.pool.length; i++) this.pool[i]!.visible = false;
    // visual HD: desenha a cena no tamanho original e amplia com o filtro de pixel art
    if (this.hd) this.app.renderer.render({ container: this.scene, target: this.hd.texture, clear: true, clearColor: [0, 0, 0, 1] });
  }

  destroy() {
    this.destroyed = true;
    window.removeEventListener('keydown', this.keyHandler);
    window.removeEventListener('keyup', this.keyUp);
    void this.ready.then(() => this.app.destroy(true, { children: true }));
  }
}
